// ============================================================================
// SIGNALR SERVICE - Client per connectar-se al hub de progrés
// Permet rebre actualitzacions en temps real de les operacions
// ============================================================================

import * as signalR from '@microsoft/signalr';

// Tipus de notificacions
export interface ProgressUpdate {
  correlationId: string;
  operationType: string;
  status: 'started' | 'progress' | 'completed' | 'error';
  percentComplete: number;
  message: string;
  messageKey?: string;
  messageParams?: Record<string, string | number>;
  shopDomain?: string;
  data?: unknown;
}

export interface OperationCompleted {
  correlationId: string;
  operationType: string;
  success: boolean;
  message: string;
  messageKey?: string;
  messageParams?: Record<string, string | number>;
  itemsProcessed: number;
  durationMs: number;
  shopDomain?: string;
  data?: unknown;
}

export interface OperationError {
  correlationId: string;
  operationType: string;
  errorMessage: string;
  shopDomain?: string;
}

// Callbacks
type ProgressCallback = (update: ProgressUpdate) => void;
type CompletedCallback = (completed: OperationCompleted) => void;
type ErrorCallback = (error: OperationError) => void;
type ConnectionCallback = (state: signalR.HubConnectionState) => void;

class SignalRService {
  private connection: signalR.HubConnection | null = null;
  private shopDomain: string | null = null;
  private subscribedOperations: Set<string> = new Set();
  
  // Callbacks
  private progressCallbacks: ProgressCallback[] = [];
  private completedCallbacks: CompletedCallback[] = [];
  private errorCallbacks: ErrorCallback[] = [];
  private connectionCallbacks: ConnectionCallback[] = [];
  
  // Retry configuration
  private maxRetries = 5;
  private retryCount = 0;

  async connect(shopDomain: string): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      // Already connected - just join the new shop group if different
      if (this.shopDomain !== shopDomain) {
        if (this.shopDomain) {
          await this.connection.invoke('LeaveShopGroup', this.shopDomain);
        }
        await this.connection.invoke('JoinShopGroup', shopDomain);
        this.shopDomain = shopDomain;
      }
      return;
    }

    // Get base URL from current location or use relative path
    const baseUrl = window.location.origin;
    const hubUrl = `${baseUrl}/hubs/progress`;
    
    console.log('[SignalR] === CONNECTING ===');
    console.log('[SignalR] Base URL:', baseUrl);
    console.log('[SignalR] Hub URL:', hubUrl);
    console.log('[SignalR] Shop domain:', shopDomain);

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        withCredentials: true,
        skipNegotiation: false,
        transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          // Exponential backoff: 0s, 2s, 4s, 8s, 16s, then null (stop)
          if (retryContext.previousRetryCount >= this.maxRetries) {
            return null;
          }
          return Math.min(1000 * Math.pow(2, retryContext.previousRetryCount), 30000);
        }
      })
      .configureLogging(signalR.LogLevel.Information)
      .build();

    // Setup event handlers
    this.setupEventHandlers();

    try {
      await this.connection.start();
      console.log('[SignalR] Connected to progress hub at:', hubUrl);
      console.log('[SignalR] Connection ID:', this.connection.connectionId);
      
      // Join shop group
      console.log('[SignalR] Joining shop group:', shopDomain);
      await this.connection.invoke('JoinShopGroup', shopDomain);
      this.shopDomain = shopDomain;
      this.retryCount = 0;
      console.log('[SignalR] Successfully joined shop group:', shopDomain);
      
      this.notifyConnectionState(this.connection.state);
    } catch (error) {
      console.error('[SignalR] Failed to connect:', error);
      this.retryCount++;
      
      if (this.retryCount < this.maxRetries) {
        // Retry after delay
        setTimeout(() => this.connect(shopDomain), 2000 * this.retryCount);
      }
      throw error;
    }
  }

  private setupEventHandlers(): void {
    if (!this.connection) return;

    // Progress updates
    this.connection.on('OperationStarted', (update: ProgressUpdate) => {
      console.log('[SignalR] *** RECEIVED OperationStarted ***:', JSON.stringify(update));
      this.progressCallbacks.forEach(cb => cb(update));
    });

    this.connection.on('ProgressUpdate', (update: ProgressUpdate) => {
      console.log('[SignalR] *** RECEIVED ProgressUpdate ***:', update.percentComplete + '%', JSON.stringify(update));
      this.progressCallbacks.forEach(cb => cb(update));
    });

    this.connection.on('OperationCompleted', (completed: OperationCompleted) => {
      console.log('[SignalR] *** RECEIVED OperationCompleted ***:', JSON.stringify(completed));
      this.completedCallbacks.forEach(cb => cb(completed));
    });

    this.connection.on('OperationError', (error: OperationError) => {
      console.error('[SignalR] *** RECEIVED OperationError ***:', JSON.stringify(error));
      this.errorCallbacks.forEach(cb => cb(error));
    });

    // Connection state changes
    this.connection.onreconnecting(() => {
      console.log('[SignalR] Reconnecting...');
      this.notifyConnectionState(signalR.HubConnectionState.Reconnecting);
    });

    this.connection.onreconnected(async () => {
      console.log('[SignalR] Reconnected');
      this.notifyConnectionState(signalR.HubConnectionState.Connected);
      
      // Rejoin groups
      if (this.shopDomain) {
        await this.connection?.invoke('JoinShopGroup', this.shopDomain);
      }
      for (const opId of this.subscribedOperations) {
        await this.connection?.invoke('SubscribeToOperation', opId);
      }
    });

    this.connection.onclose(() => {
      console.log('[SignalR] Connection closed');
      this.notifyConnectionState(signalR.HubConnectionState.Disconnected);
    });
  }

  async subscribeToOperation(correlationId: string): Promise<void> {
    console.log('[SignalR] subscribeToOperation called:', correlationId, 'state:', this.connection?.state);
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      console.log('[SignalR] Invoking SubscribeToOperation for:', correlationId);
      await this.connection.invoke('SubscribeToOperation', correlationId);
      this.subscribedOperations.add(correlationId);
      console.log('[SignalR] Successfully subscribed to operation:', correlationId);
    } else {
      console.warn('[SignalR] Cannot subscribe - not connected. State:', this.connection?.state);
    }
  }

  async unsubscribeFromOperation(correlationId: string): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      await this.connection.invoke('UnsubscribeFromOperation', correlationId);
      this.subscribedOperations.delete(correlationId);
    }
  }

  // Subscribe to callbacks
  onProgress(callback: ProgressCallback): () => void {
    this.progressCallbacks.push(callback);
    return () => {
      this.progressCallbacks = this.progressCallbacks.filter(cb => cb !== callback);
    };
  }

  onCompleted(callback: CompletedCallback): () => void {
    this.completedCallbacks.push(callback);
    return () => {
      this.completedCallbacks = this.completedCallbacks.filter(cb => cb !== callback);
    };
  }

  onError(callback: ErrorCallback): () => void {
    this.errorCallbacks.push(callback);
    return () => {
      this.errorCallbacks = this.errorCallbacks.filter(cb => cb !== callback);
    };
  }

  onConnectionChange(callback: ConnectionCallback): () => void {
    this.connectionCallbacks.push(callback);
    return () => {
      this.connectionCallbacks = this.connectionCallbacks.filter(cb => cb !== callback);
    };
  }

  private notifyConnectionState(state: signalR.HubConnectionState): void {
    this.connectionCallbacks.forEach(cb => cb(state));
  }

  async disconnect(): Promise<void> {
    if (this.connection) {
      await this.connection.stop();
      this.connection = null;
      this.shopDomain = null;
      this.subscribedOperations.clear();
    }
  }

  getConnectionState(): signalR.HubConnectionState {
    return this.connection?.state ?? signalR.HubConnectionState.Disconnected;
  }
}

// Singleton instance
export const signalRService = new SignalRService();
export default signalRService;
